import React, { useCallback, useEffect, useRef, useState } from "react"
import { Card, Col, Dropdown, Modal, Row } from "react-bootstrap"
import { parsedUser } from "../../common/GetCurrentUser"
import API from "../../common/API"
import moment from "moment"
import Settings from "../../common/Settings"
import GoogleMapReact from "google-map-react"
import PinComponent from "../Common/PinComponent"
import { useHistory } from "react-router-dom"
import { Routes } from "../../routes"
import { useTranslation } from "react-i18next"
import { confirmAlert } from "react-confirm-alert"
import { userAvatar } from "@/common/helpers"
import {
  createRealtimeConnection,
  startConnectionSafe,
  stopConnectionSafe,
} from "@/services/realtime"

const realtimeEvents = [
  "onMessage",
  "onChatMessage",
  "ReceiveMessage",
  "ReceiveChatMessage",
  "NewMessage",
  "onNotification",
  "onUnreadCountChanged",
  "UnreadCountChanged",
]

const buildChatImageUrl = picture => {
  if (!picture) return null
  const base = Settings.BasePathForAPI || Settings.BasePath
  return `${base}file/getThumbnail?filename=${encodeURIComponent(picture)}`
}

const resolveMemberName = member => {
  if (!member) return ""
  const directName = (member.name || "").trim()
  if (directName) return directName
  const first = (member.firstName || "").trim()
  const last = (member.lastName || "").trim()
  return `${first} ${last}`.trim()
}

const Conversations = props => {
  const GoogleMapRef = useRef(null)
  const messagesEndRef = useRef(null)
  const messagesPollingRef = useRef(null)
  const conversationsPollingRef = useRef(null)
  const connectionRef = useRef(null)
  const joinedRoomRef = useRef(null)

  const [records, setRecords] = useState([])
  const [record, setRecord] = useState(null)
  const [message, setMessage] = useState("")
  const [messages, setMessages] = useState([])
  const [selectedOrganization, setSelectedOrganization] = useState("")
  const [organizations, setOrganizations] = useState([])
  const history = useHistory()
  const { t } = useTranslation("global")
  const user = parsedUser()

  const notifyUnreadChanged = useCallback(() => {
    if (props.onUnreadCountChanged) {
      props.onUnreadCountChanged()
    }
  }, [props])

  const stopPolling = useCallback(() => {
    if (messagesPollingRef.current) {
      clearInterval(messagesPollingRef.current)
      messagesPollingRef.current = null
    }
    if (conversationsPollingRef.current) {
      clearInterval(conversationsPollingRef.current)
      conversationsPollingRef.current = null
    }
  }, [])

  const bindDataRemote = useCallback(async () => {
    if (!user?.ID) return
    try {
      const query = `userId=${user.ID}&OrganizationID=${selectedOrganization || ""}`
      const request = await API.getAction("Chat/Chats", query)
      if (request.data.status === "ok") {
        setRecords(request.data.response || [])
      }
    } catch (error) {
      console.error("Error loading chats:", error)
    }
  }, [selectedOrganization, user?.ID])

  const bindDataDetailsRemote = useCallback(async () => {
    if (!props.chatId || !user?.ID) return
    try {
      const request = await API.getAction(
        "Chat/ChatDetails",
        "id=" + props.chatId + "&userId=" + user.ID
      )
      if (request.data.status === "ok") {
        const details = request.data.response || []
        if (details.length > 0) {
          setRecord(details[0])
        }
      }
    } catch (error) {
      console.error("Error loading chat details:", error)
    }
  }, [props.chatId, user?.ID])

  const bindDataConversationRemote = useCallback(async () => {
    if (!record?.id || !user?.ID) return
    try {
      const request = await API.getAction(
        "Chat/Conversation",
        "id=" + record.id + "&userId=" + user.ID
      )
      if (request.data.status === "ok") {
        setMessages(request.data.response || [])
        notifyUnreadChanged()
      }
    } catch (error) {
      console.error("Error loading messages:", error)
    }
  }, [record?.id, notifyUnreadChanged, user?.ID])

  const requestOrganizations = useCallback(async () => {
    const currentUser = parsedUser()
    if (!currentUser?.ID) return
    try {
      const requestAPI = await API.getAction(
        "Organization/OrganizationsWhereTheUserBelong?AccountId=" + currentUser.ID
      )
      if (requestAPI.data.status === "ok") {
        setOrganizations(requestAPI.data.response || [])
      }
    } catch (error) {
      console.error("Error loading organizations:", error)
    }
  }, [])

  const sendMessage = async () => {
    if (!record?.id || !user?.ID || !message.trim()) return
    const query = {
      chatId: record.id,
      senderId: user.ID,
      message: message.trim(),
    }
    setMessage("")
    try {
      const request = await API.postAction("Chat/SendMessage", query)
      if (request.data.status === "ok") {
        await bindDataConversationRemote()
        notifyUnreadChanged()
      }
    } catch (error) {
      console.error("Error sending message:", error)
    }
  }

  const leaveChat = chat => {
    confirmAlert({
      closeOnClickOutside: false,
      message: t("Are you sure you want to leave?") ?? "",
      buttons: [
        {
          label: t("Yes"),
          onClick: async () => {
            const currentUser = parsedUser()
            if (!currentUser?.ID) return
            const requestAPI = await API.getAction(
              "chat/leaveChat?UserId=" + currentUser.ID + "&ChatId=" + chat.id
            )
            if (requestAPI.data.status === "ok") {
              await bindDataRemote()
              notifyUnreadChanged()
            }
          },
        },
        {
          label: "No",
          onClick: () => null,
        },
      ],
    })
  }

  const handleRealtimeRefresh = useCallback(() => {
    if (record?.id) {
      bindDataConversationRemote()
    } else {
      bindDataRemote()
    }
    notifyUnreadChanged()
  }, [bindDataConversationRemote, bindDataRemote, notifyUnreadChanged, record?.id])

  const listOfConversations = () => {
    return records.map((item, index) => {
      const chatTitle = item?.title || t("Untitled")
      const chatImageUrl = buildChatImageUrl(item?.picture)
      const participants =
        item.members
          ?.filter(x => String(x.memberId) !== String(user.ID))
          ?.map(member => resolveMemberName(member))
          ?.filter(Boolean)
          ?.join(", ") || ""

      return (
        <Card
          key={item.id || index}
          className={`my-2 shadow-sm clickable chat-conversation-card ${record?.id === item.id ? "chat-conversation-card-active" : ""}`}
          onClick={() => setRecord(item)}
        >
          <Card.Body className="p-2">
            <Row>
              <Col className="col-auto">
                <img loading="lazy"
                  alt={chatTitle}
                  src={chatImageUrl || userAvatar(chatTitle, 50)}
                  className="img-fluid rounded-circle mx-0"
                  style={{ height: 50, width: 50 }}
                  onError={event => {
                    event.currentTarget.src = userAvatar(chatTitle, 50)
                  }}
                />
              </Col>
              <Col className="pe-0">
                <Row>
                  <Col>
                    <div className="d-flex justify-content-between align-items-start gap-2">
                      <b className="text-truncate">{chatTitle}</b>
                      {item.lastMessageDate && (
                        <small className="text-muted text-nowrap">
                          {moment(item.lastMessageDate).fromNow()}
                        </small>
                      )}
                    </div>
                    {participants && (
                      <small className="text-muted d-block text-truncate">
                        {participants}
                      </small>
                    )}
                    <small className="text-muted d-block text-truncate">
                      {item.lastMessage || t("No messages yet")}
                    </small>
                  </Col>
                </Row>
              </Col>
              <Col className="col-auto text-end">
                {item.unreadMessages > 0 && (
                  <div className="badge bg-danger">{item.unreadMessages}</div>
                )}
                <Dropdown>
                  <Dropdown.Toggle variant="link">
                    <i className="fas fa-ellipsis-v" />
                  </Dropdown.Toggle>
                  <Dropdown.Menu>
                    <Dropdown.Item onClick={() => leaveChat(item)}>
                      {t("Leave chat")}
                    </Dropdown.Item>
                  </Dropdown.Menu>
                </Dropdown>
              </Col>
            </Row>
          </Card.Body>
        </Card>
      )
    })
  }

  const listMessages = () => {
    let actualUser = null
    return messages.map((item, index) => {
      const isMine = String(item.senderId) === String(user?.ID)
      const messageRow = (
        <div key={item.id || index} className={`chat-message-row ${isMine ? "mine" : "theirs"}`}>
          {actualUser !== item.senderId && !isMine && (
            <p className="m-0 mt-3 p-0 fw-bold">
              {item.senderName} {item.senderLastName}
            </p>
          )}
          <div className={`message chat-message-bubble ${isMine ? "chat-message-bubble-mine" : "chat-message-bubble-theirs"}`}>
            <div className="flex-row text-break">
              {item.message}
            </div>
            {item.created && (
              <small className={`d-block mt-1 ${isMine ? "text-white-50" : "text-muted"}`}>
                {moment(item.created).fromNow()}
              </small>
            )}
          </div>
        </div>
      )

      actualUser = item.senderId
      return messageRow
    })
  }

  useEffect(() => {
    stopPolling()

    if (record?.id) {
      bindDataConversationRemote()
      messagesPollingRef.current = setInterval(bindDataConversationRemote, 5000)
    } else {
      bindDataRemote()
      conversationsPollingRef.current = setInterval(bindDataRemote, 15000)
    }

    return stopPolling
  }, [bindDataConversationRemote, bindDataRemote, record?.id, stopPolling])

  useEffect(() => {
    if (props.chatId) {
      bindDataDetailsRemote()
    }
  }, [bindDataDetailsRemote, props.chatId])

  useEffect(() => {
    bindDataRemote()
    requestOrganizations()
  }, [bindDataRemote, requestOrganizations])

  useEffect(() => {
    if (!user?.ID) return

    const connection = createRealtimeConnection()
    connectionRef.current = connection

    realtimeEvents.forEach(eventName => connection.on(eventName, handleRealtimeRefresh))
    connection.onreconnected(handleRealtimeRefresh)

    startConnectionSafe(connection).then(isConnected => {
      if (isConnected) {
        connection.invoke("RegisterUser", String(user.ID)).catch(() => null)
        handleRealtimeRefresh()
      }
    })

    return () => {
      realtimeEvents.forEach(eventName => connection.off(eventName, handleRealtimeRefresh))
      stopConnectionSafe(connection)
    }
  }, [handleRealtimeRefresh, user?.ID])

  useEffect(() => {
    const connection = connectionRef.current
    if (!connection || !user?.ID) {
      return
    }

    const previousRoom = joinedRoomRef.current
    const nextRoom = record?.id ? String(record.id) : null

    if (previousRoom && previousRoom !== nextRoom) {
      connection.invoke("LeaveRoom", previousRoom).catch(() => null)
      joinedRoomRef.current = null
    }

    if (nextRoom && previousRoom !== nextRoom) {
      connection.invoke("JoinRoom", nextRoom, String(user.ID)).catch(() => null)
      joinedRoomRef.current = nextRoom
    }

    if (!nextRoom && previousRoom) {
      connection.invoke("LeaveRoom", previousRoom).catch(() => null)
      joinedRoomRef.current = null
    }
  }, [record?.id, user?.ID])

  useEffect(() => {
    if (messages.length > 0 && messagesEndRef.current) {
      messagesEndRef.current.scrollIntoView({ behavior: "smooth", block: "end" })
    }
  }, [messages])

  return (
    <Modal
      show={props.show}
      onHide={props.toggle}
      className="chatContainer modalRight chat-modal-pleelo"
    >
      <Modal.Header closeButton>
        {record ? (
          <Row className="mx-0">
            <Col className="col-auto">
              <button
                className="btn me-2 btn-link"
                onClick={() => setRecord(null)}
                type="button"
              >
                <i className="fa fa-chevron-left" />
              </button>
              <img loading="lazy"
                alt={record.title || t("Chat")}
                src={buildChatImageUrl(record.picture) || userAvatar(record.title || t("Chat"), 22)}
                className="img-fluid rounded mx-0 d-inline me-2"
                style={{ height: 22, width: 22 }}
                onError={event => {
                  event.currentTarget.src = userAvatar(record.title || t("Chat"), 22)
                }}
              />

              <span
                className="clickable"
                onClick={() => {
                  history.push(Routes.PlaceDetails.clean + record.placeId)
                }}
              >
                <b> {record.title}</b>
              </span>
            </Col>
          </Row>
        ) : (
          <Row>
            <Col className="col-auto pt-2">
              <b>{t("Conversations")}</b>
            </Col>
            <Col>
              <select
                className="form-control form-control-sm"
                id="organization"
                value={selectedOrganization}
                onChange={e => setSelectedOrganization(e.target.value)}
              >
                <option value="">-</option>
                {organizations.map((x, index) => (
                  <option value={x.id} key={index}>
                    {x.name}
                  </option>
                ))}
              </select>
            </Col>
          </Row>
        )}
      </Modal.Header>
      <Modal.Body
        className="p-0"
        style={{ overflow: "auto", minHeight: "81vh" }}
      >
        <Row className="mx-0">
          <Col xs={12} className="my-2">
            {!record ? (
              listOfConversations()
            ) : (
              <>
                <GoogleMapReact
                  ref={GoogleMapRef}
                  yesIWantToUseGoogleMapApiInternals
                  bootstrapURLKeys={{ key: Settings.GoogleMapsKey }}
                  style={{
                    width: "100%",
                    height: "170px",
                    position: "relative",
                  }}
                  center={{
                    lat: record.placeLatitude,
                    lng: record.placeLongitude,
                  }}
                  defaultZoom={17}
                >
                  <PinComponent
                    Type={"Visit"}
                    lat={record.placeLatitude}
                    lng={record.placeLongitude}
                    text={record.title}
                  />
                </GoogleMapReact>
                {listMessages()}
                <div ref={messagesEndRef} />
              </>
            )}
          </Col>
        </Row>
      </Modal.Body>
      {record && (
        <Modal.Footer className="bg-light">
          <Row className="mx-0 w-100">
            <Col>
              <input
                type="text"
                className="form-control"
                placeholder={t("Message")}
                name="message"
                value={message}
                onChange={e => setMessage(e.target.value)}
                onKeyDown={e => {
                  if (e.key === "Enter") {
                    e.preventDefault()
                    sendMessage()
                  }
                }}
              />
            </Col>
            <Col className="text-right col-auto">
              <button
                disabled={message.trim().length === 0}
                className="btn btn-primary"
                type="button"
                onClick={sendMessage}
              >
                {t("Send")}
              </button>
            </Col>
          </Row>
        </Modal.Footer>
      )}
    </Modal>
  )
}

export default Conversations
